import { jsx as _jsx } from "react/jsx-runtime";
import { GeolocDialog } from '@sezane/front-components';
import Cookies from 'js-cookie';
import { createRoot } from 'react-dom/client';
import analytics from '../../analytics';
import { getDefaultCountry, getMapCountrySite, getUserAgentCountry, GLOBALE_COUNTRIES_CURRENCIES, } from '../../countries';
import { getSites } from '../../sites';
import translate from '../../translate';
import { isWebview } from '../../utils';
const GLOBALE_COOKIE = 'GlobalE_Data';
const mapCountrySite = getMapCountrySite();
export const getLocale = () => {
    const globalEData = getGlobalEData();
    return globalEData?.locale || document.documentElement.lang.split('-')[0];
};
// we don't want to use the js-cookie default encoder because
// we want: %7B%22countryISO%22:%22FR%22,%22currencyCode%22:%22EUR%22%7D
// and the result is {%22countryISO%22:%22FR%22%2C%22currencyCode%22:%22EUR%22}
// so we need to encodeURI before and use this cookieWithConverter
const cookieWithConverter = Cookies.withConverter({
    write(value) {
        return value;
    },
    read(value) {
        return value;
    },
});
const GLOBALE_BANNER_DIALOG = 'dialog-globalE-banner';
// eslint-disable-next-line import/no-mutable-exports
export let GLOBALE_INITIALIZED = false;
const getGlobalECookieParams = () => {
    const domain = window.location.hostname.replace(/(www-nocache|www)/, '');
    return {
        domain,
        path: '/',
        'Max-Age': (182 * 24 * 60 * 60).toString(), // 6 month
        'Same-Site': 'Lax',
    };
};
export const getGlobalEData = () => {
    const globalECookie = cookieWithConverter.get(GLOBALE_COOKIE);
    let globalEData;
    try {
        try {
            globalEData = JSON.parse(decodeURIComponent(globalECookie));
        }
        catch {
            // because there are some cookies with double encoded value
            // we try to decode it twice and put the result of the first decode
            // in the cookie
            const firstDecodeGlobalECookie = decodeURIComponent(globalECookie);
            globalEData = JSON.parse(decodeURIComponent(firstDecodeGlobalECookie));
            cookieWithConverter.set(GLOBALE_COOKIE, firstDecodeGlobalECookie, getGlobalECookieParams());
        }
    }
    catch {
        // eslint-disable-next-line no-console
        console.error("Couldn't read GlobalE cookie value.", globalECookie);
    }
    return globalEData;
};
export const setGlobalECookie = (countryISO, currencyCode, locale, regionCode) => {
    const cookieValue = encodeURI(JSON.stringify({
        countryISO,
        currencyCode,
        regionCode,
        locale,
    }));
    cookieWithConverter.set(GLOBALE_COOKIE, cookieValue, getGlobalECookieParams());
};
const isGlobalE = () => {
    // @ts-ignore
    const globalECountries = window.globalECountries || [];
    const globalEData = getGlobalEData();
    return globalEData && globalECountries.includes(globalEData.countryISO);
};
export const addGlobalEScripts = () => {
    // @ts-ignore
    const globalEJSUrl = window.globalEJSUrl;
    // @ts-ignore
    const globalECSSUrl = window.globalECSSUrl;
    const userAgentCountry = getUserAgentCountry();
    if (userAgentCountry) {
        setGlobalECookie(userAgentCountry.code, userAgentCountry.currency);
    }
    if (userAgentCountry || isGlobalE()) {
        if (globalEJSUrl) {
            const script = document.createElement('script');
            script.src = globalEJSUrl;
            script.async = true;
            document.body.appendChild(script);
        }
        if (globalECSSUrl) {
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = globalECSSUrl;
            document.body.appendChild(link);
        }
    }
};
export const handleGlobalEBanner = async (manual = false) => {
    if (isWebview())
        return;
    const banner = document.getElementById(GLOBALE_BANNER_DIALOG);
    if (!banner)
        return;
    if (window.localStorage.getItem('disableGlobalEBanner')) {
        return;
    }
    const sites = await getSites();
    const globalEData = getGlobalEData();
    const defaultCountry = getDefaultCountry();
    if (!defaultCountry)
        return;
    const defaultLocale = getLocale();
    // Show banner if detected country site != current site
    const actualSiteCode = mapCountrySite[globalEData?.countryISO];
    if (!manual && actualSiteCode === banner.dataset.site) {
        return;
    }
    if (!manual && GLOBALE_INITIALIZED) {
        return;
    }
    const site = sites.sites.find((s) => s.code === actualSiteCode);
    const countries = Object.entries(sites.mappingCountrySite).map(([countryCode, siteCode]) => {
        const siteForCountry = sites.sites.find((s) => s.code === siteCode);
        return {
            code: countryCode,
            locales: siteForCountry.availableLocales.map((locale) => locale.code),
            defaultLocale: siteForCountry.defaultLocale.code,
        };
    });
    const initialCountry = (globalEData?.countryISO || defaultCountry.code);
    GLOBALE_INITIALIZED = true;
    const bannerRoot = createRoot(banner);
    bannerRoot.render(_jsx(GeolocDialog, { open: true, countries: countries, onClose: () => {
            if (manual) {
                window.location.reload();
                return;
            }
            setGlobalECookie(defaultCountry.code, defaultCountry.currencyCode, defaultLocale, undefined);
            fetch(`/${banner.dataset.site}/cart/update`, {
                method: 'POST',
                credentials: 'same-origin',
            }).then(() => {
                window.location.reload();
            });
        }, translations: {
            title: translate('front.geoloc_dialog.title'),
            subtitle: translate('front.geoloc_dialog.subtitle'),
            selectCountry: translate('front.geoloc_dialog.select_country'),
            selectLanguage: translate('front.geoloc_dialog.select_language'),
            button: translate('front.geoloc_dialog.button'),
            footer: translate('front.geoloc_dialog.footer'),
        }, initialCountry: initialCountry, initialLocale: defaultLocale, onSubmit: (country, language) => {
            const selectedCountry = GLOBALE_COUNTRIES_CURRENCIES.find((c) => c.country === country);
            let redirectLink = '';
            let linkSite = site || mapCountrySite[defaultCountry.code];
            let cookieCountryCode = globalEData?.countryISO;
            let cookieCurrencyCode = globalEData?.currencyCode;
            let cookieRegionCode = globalEData?.regionCode;
            const regionCode = selectedCountry.country === cookieCountryCode ? cookieRegionCode : undefined;
            if (selectedCountry) {
                linkSite = mapCountrySite[country] || site;
                cookieCountryCode = country;
                cookieCurrencyCode = selectedCountry.currency;
            }
            const referer = window.location.pathname;
            const searchParams = new URLSearchParams();
            searchParams.append('refresh_order', 'true');
            if (referer && referer !== '/') {
                searchParams.append('referer', referer);
            }
            const searchParamsString = searchParams.toString();
            redirectLink = `/${linkSite}-${language}/${searchParamsString ? `?${searchParamsString}` : ''}`;
            setGlobalECookie(cookieCountryCode, cookieCurrencyCode, language, regionCode);
            // add 50ms delay to let cookie to be set.
            window.setTimeout(() => {
                window.location.href = redirectLink;
            }, 50);
        } }));
    analytics.popinGlobalE();
};
